import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { MoreHoriz, PlayArrow, Public } from "@mui/icons-material";
import { getMediaKind } from "../util";

interface Props {
  media?: string[];
  caption?: string;
  pageName?: string;
  timeLabel?: string;
  avatarUrl?: string;
}

export default function FacebookStoriesPreview({
  media = [],
  caption,
  pageName = "Your Page",
  timeLabel = "Just now",
  avatarUrl
}: Props) {
  const first = media[0];
  const isVideo = first ? getMediaKind(first) === "video" : false;

  return (
    <Box sx={styles.root}>
      {first ? (
        <Box
          component={isVideo ? "video" : "img"}
          src={first}
          autoPlay={isVideo ? true : undefined}
          muted={isVideo ? true : undefined}
          loop={isVideo ? true : undefined}
          playsInline={isVideo ? true : undefined}
          sx={styles.media}
        />
      ) : (
        <Box sx={styles.noMedia}>
          <Typography variant="body2">No media for Stories</Typography>
        </Box>
      )}

      <Box sx={styles.topGradient} />

      <Box sx={styles.progressBar}>
        <Box sx={styles.progressTrack}>
          <Box sx={styles.progressFill} />
        </Box>
      </Box>

      <Box sx={styles.header}>
        <Avatar src={avatarUrl} sx={styles.avatar}>
          {pageName?.[0] ?? "P"}
        </Avatar>

        <Box sx={styles.headerText}>
          <Typography sx={styles.pageName}>
            {pageName}
          </Typography>
          <Typography sx={styles.timeLabel}>
            {timeLabel} <Public fontSize="inherit" />
          </Typography>
        </Box>

        <IconButton size="small" sx={styles.iconBtn}>
          <PlayArrow fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.iconBtn}>
          <MoreHoriz fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.bottomGradient} />
      {caption ? (
        <Box sx={styles.captionBox}>
          <Typography sx={styles.caption}>
            {caption}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

const styles = {
  root: {
    position: "relative",
    width: "100%",
    maxWidth: 360,
    aspectRatio: "9 / 16",
    bgcolor: "black",
    borderRadius: 2,
    overflow: "hidden",
    color: "common.white",
    boxShadow: 1
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    bgcolor: "black"
  },
  noMedia: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "grey.500"
  },
  topGradient: {
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 140,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0) 100%)"
  },
  progressBar: {
    position: "absolute",
    top: 8,
    left: 8,
    right: 8
  },
  progressTrack: {
    height: 3,
    bgcolor: "rgba(255,255,255,0.35)",
    borderRadius: 9999
  },
  progressFill: {
    width: "60%",
    height: "100%",
    bgcolor: "common.white",
    borderRadius: 9999
  },
  header: {
    position: "absolute",
    top: 16 + 8,
    left: 8,
    right: 8,
    display: "flex",
    alignItems: "center",
    gap: 1
  },
  avatar: {
    width: 32,
    height: 32,
    bgcolor: "#1976d2",
    fontWeight: 700
  },
  headerText: {
    flexGrow: 1,
    minWidth: 0
  },
  pageName: {
    variant: "body2",
    fontWeight: 600,
    noWrap: true
  },
  timeLabel: {
    variant: "caption",
    opacity: 0.85,
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5
  },
  iconBtn: {
    color: "common.white"
  },
  bottomGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 140,
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0) 100%)"
  },
  captionBox: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 12
  },
  caption: {
    variant: "body2",
    whiteSpace: "pre-wrap"
  }
};
